//@flow
import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import { TeamId } from 'common/models/internal/TeamId';
import I18nHelper from 'common/helpers/I18nHelper';
import { ApiError } from 'common/modules/api';
import * as Immutable from 'immutable';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { WeekType } from 'common/models/internal/Plan';
import isString from 'lodash/isString';
import { ContactType } from 'common/models/internal/Contact';
import StringUtils from 'common/utils/StringUtils';
import { TeamStatusEnum } from 'common/enums/TeamEnum';
import startCase from 'lodash/startCase';

import { CustomField } from 'common/models/internal/EntitySettings';
import { Holiday } from 'common/models/internal/MasterOutlet';
import { MultiDaySlot } from 'common/models/internal/MultiDaySlot';
import { ResourceLimit } from 'common/models/internal/ResourceLimit';
import { TimeSlot } from 'common/models/internal/TimeSlot';
import { BreakShift } from 'common/models/internal/BreakShift';
import { SkillId } from 'common/models/internal/SkillId';
import { memoized, memoizedImmutable } from 'common/utils/Memoizer';
import { ContactId } from 'common/models/internal/ContactId';
import { TransporterId } from 'common/models/internal/TransporterId';
import { ShiftId } from 'common/models/internal/ShiftId';
import {
    WeekShortEnum,
    WeekDayShortEnum,
    WeekDayEnum,
    WeekFullEnum,
    WeekDayFullEnum
} from 'common/enums/WeekEnums';
import { volumeUnitOptions } from 'common/enums/VolumeUnitEnum';
import { VehicleModelId } from 'common/models/internal/VehicleModelId';
import { UnitEnum } from 'common/models/internal/RiderPersonaResourceLimit';
import type { EntityImportHeaderModelType } from 'common/models/flow/EntityImportTable';
import type { MasterVehicleModel } from 'common/models/internal/MasterVehicleModel';
import type { ResourceLimitModelType } from 'common/models/flow/ResourceLimit';
import DateTimeUtils, {
    CustomDateFormats,
    DateInputFormats
} from 'common/utils/DateTimeUtils';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
const RESOURCE_PATH = ['limits', 'resourceLimits'];

export class EntityImportDataHelper {
    static processMinutesToSeconds({ cellValue }) {
        return NumberUtils.isNumeric(cellValue) ? cellValue * 60 : cellValue;
    }
    static processKiloMetersToMeters({ cellValue }) {
        return NumberUtils.isNumeric(cellValue) ? cellValue * 1000 : cellValue;
    }
    static processTeam({ cellValue, clientId }) {
        return cellValue
            ? [
                  {
                      teamId: cellValue.trim(),
                      clientId
                  }
              ]
            : [];
    }

    static processShiftIds({ cellValue, clientId }) {
        return cellValue
            ? cellValue
                  .split(',')
                  .filter(shiftId => shiftId && shiftId.trim())
                  .map(shiftId => {
                      return {
                          shiftId: shiftId.trim(),
                          clientId
                      };
                  })
            : [];
    }

    static processTeams({ cellValue, clientId }) {
        return cellValue
            ? cellValue.split(',').reduce((currentTeams, teamId) => {
                  let newTeams = [...currentTeams];
                  const trimmedId = teamId && teamId.trim();
                  if (!DataUtils.isEmpty(trimmedId)) {
                      newTeams = [
                          ...newTeams,
                          {
                              clientId,
                              teamId: trimmedId
                          }
                      ];
                  }
                  return newTeams;
              }, [])
            : [];
    }

    static processSkill({ cellValue, clientId }) {
        return cellValue
            ? [
                  {
                      id: cellValue.trim(),
                      clientId
                  }
              ]
            : [];
    }
    static processSkills({ cellValue, clientId }) {
        return cellValue
            ? cellValue
                  .split(',')
                  .filter(skillId => skillId?.trim())
                  .map(skillId => {
                      return {
                          id: skillId.trim(),
                          clientId
                      };
                  })
            : [];
    }

    static processContactTeams({ cellValue, clientId }) {
        return cellValue
            ? cellValue
                  .split(',')
                  .filter(teamId => teamId?.trim())
                  .map(teamId => {
                      return {
                          teamId: teamId.trim(),
                          clientId
                      };
                  })
            : [];
    }

    static processDate({ cellValue }) {
        return (
            DateTimeUtils.customFormat(cellValue, CustomDateFormats.API_DATE, [
                'DD/MM/YYYY',
                'DD/MM/YY'
            ]) || cellValue
        );
    }

    static processUpperCaseId({ cellValue }) {
        // for manufacturing_plan, also filtering possible trailing space
        return cellValue.replace(' ', '_').toUpperCase();
    }

    @memoized()
    static getSlots(start, end) {
        const updatedStart =
            DateTimeUtils.customFormat(
                start,
                CustomDateFormats.API_TIME,
                DateInputFormats.IMPORT_TIME
            ) || start;
        const updatedEnd =
            DateTimeUtils.customFormat(
                end,
                CustomDateFormats.API_TIME,
                DateInputFormats.IMPORT_TIME
            ) || end;
        return {
            start: updatedStart,
            end: updatedEnd
        };
    }

    static processTimeSlot({ cellValue }) {
        const [start, end] = cellValue && cellValue.split('-');
        let dateObj = {};
        if (start && end) {
            dateObj = EntityImportDataHelper.getSlots(start.trim(), end.trim());
        }
        if (!('start' in dateObj) && cellValue) {
            /**
             * Case for fixing ES-14124
             * when all slots are empty we throw one slot is required and
             * when user enters some value and that is empty we should not show single slot is required
             */
            dateObj.start = cellValue;
        }
        return dateObj;
    }

    static processTimeSlots({ cellValue, header }) {
        const newSlots = !DataUtils.isEmpty(cellValue)
            ? cellValue.split(',').map(slot => {
                  return EntityImportDataHelper.processTimeSlot({
                      cellValue: slot
                  });
              })
            : [];
        return {
            dayOfWeek: header.id,
            slots: newSlots
        };
    }

    static processBreaks({ cellValue }) {
        if (!DataUtils.isEmpty(cellValue)) {
            let defaultDuration = 10; // 10 minutes
            const breakSlots = cellValue.split(',').map(breakSlot => {
                let breakSlotWithoutDuration = breakSlot;
                if (breakSlot.split(':').length > 3) {
                    const breakDurationIndex = breakSlot.lastIndexOf(':');
                    breakSlotWithoutDuration = breakSlot.substring(
                        0,
                        breakDurationIndex
                    );
                    const breaksDuration = breakSlot.split(':').pop();
                    defaultDuration =
                        breakSlot.split(':').length > 3 &&
                        !DataUtils.isEmpty(breaksDuration)
                            ? breaksDuration
                            : defaultDuration;
                }
                return EntityImportDataHelper.processTimeSlotToShiftInstant({
                    slot: breakSlotWithoutDuration,
                    duration: NumberUtils.isNumeric(defaultDuration)
                        ? defaultDuration * 60
                        : defaultDuration
                });
            });
            return breakSlots.map(breakSlot => {
                return {
                    ...breakSlot
                };
            });
        }
        return [];
    }

    /* internal only */
    static processTimeSlotToShiftInstant({ slot, duration }) {
        let [start, end] = slot && slot.split('-');
        if (start && end) {
            start =
                DateTimeUtils.customFormat(
                    start.trim(),
                    CustomDateFormats.API_TIME,
                    DateInputFormats.IMPORT_TIME
                ) || start;
            end =
                DateTimeUtils.customFormat(
                    end.trim(),
                    CustomDateFormats.API_TIME,
                    DateInputFormats.IMPORT_TIME
                ) || end;
        }
        return {
            startShiftInstant: {
                time: start,
                day: 1
            },
            endShiftInstant: {
                time: end,
                day: 1
            },
            duration
        };
    }

    /* validations begin */
    /* generic entity validators */
    static validateEntityTimeSlots({ entity, header }) {
        const error = entity.getValidationMessage('timeSlots');
        if (error) {
            return error;
        }
        const timeSlot = entity.timeSlots.find(
            slot => slot.dayOfWeek === header.id
        );
        return timeSlot && timeSlot.getValidationMessage('slots');
    }

    static validateCustomResource({
        entity,
        header
    }: {
        entity: MasterVehicleModel,
        header: EntityImportHeaderModelType
    }): ?string {
        const { customFieldsKey, id } = header;
        const resourceName = id.split('-').pop();
        const resourceLimit = entity.limits?.resourceLimits.find(
            resource => resource.name === resourceName
        );
        return (
            resourceLimit && resourceLimit.getValidationMessage(customFieldsKey)
        );
    }

    static validateCustomProperty({ customFields, entity, header }) {
        let error;
        if (entity.customProperties) {
            const value = entity.customProperties.get(header.id);
            if (!DataUtils.isEmpty(value)) {
                error = EntityImportDataHelper.validateSingleCustomProperty({
                    value,
                    key: header.id,
                    customFields
                });
            }
        }
        return error;
    }

    static validateModelCustomProperty({ customFields, entity, header }) {
        let error;
        const value = entity.getIn(header.getAccessor());
        if (!DataUtils.isEmpty(value)) {
            error = EntityImportDataHelper.validateSingleCustomProperty({
                value,
                key: header.customFieldsKey,
                customFields
            });
        }
        return error;
    }
    static getCustomPropertiesProcessor(field: CustomField) {
        return ({ cellValue }) => field.getActualValue(cellValue) || cellValue;
    }

    static validateSingleCustomProperty({ value, key, customFields }) {
        const customField = customFields.find(field => {
            return field.key === key;
        });
        return customField && customField.validate(value);
    }

    @memoizedImmutable()
    static getActiveTeamsList(teams: Immutable.List) {
        return teams
            .filter(team => team.status === TeamStatusEnum.Active)
            .map(team => team.teamId)
            .toList();
    }

    static validateTeamIds({ teams, entity }) {
        const validTeams = EntityImportDataHelper.getActiveTeamsList(teams);
        return TeamId.getTeamIdsValidationMessage({
            validTeams,
            teamIds: entity.teams
        });
    }

    static validateContactTeamIds({ teams, entity }) {
        const validTeams = teams.map(team => team.teamId).toList();
        if (entity.contact) {
            return TeamId.getTeamIdsValidationMessage({
                validTeams,
                teamIds: entity.contact.teams
            });
        }
    }

    static validateTeamId({ teams, entity }) {
        const validTeams = EntityImportDataHelper.getActiveTeamsList(teams);
        return TeamId.getTeamIdsValidationMessage({
            validTeams,
            teamIds: Immutable.List([entity.teamId])
        });
    }

    static validateGroup({ group, id }) {
        return (
            group &&
            group.getValidationMessage &&
            group.getValidationMessage(id)
        );
    }

    static getBooleanProcessor(
        { positiveLabelKey, negativeLabelKey } = {
            positiveLabelKey: 'masterImportTemplateValues.booleanValues.yes',
            negativeLabelKey: 'masterImportTemplateValues.booleanValues.no'
        }
    ) {
        return EntityImportDataHelper.processBooleanValue.bind(null, {
            positiveLabelKey,
            negativeLabelKey
        });
    }
    static getBooleanValidator(
        { positiveLabelKey, negativeLabelKey } = {
            positiveLabelKey: 'masterImportTemplateValues.booleanValues.yes',
            negativeLabelKey: 'masterImportTemplateValues.booleanValues.no'
        }
    ) {
        return EntityImportDataHelper.validateBooleanValue.bind(null, {
            positiveLabelKey,
            negativeLabelKey
        });
    }

    static processBooleanValue(
        { positiveLabelKey, negativeLabelKey },
        { cellValue }
    ) {
        return cellValue.toLowerCase().trim() ===
            nsTranslate(positiveLabelKey).toLowerCase()
            ? true
            : cellValue.toLowerCase().trim() ===
              nsTranslate(negativeLabelKey).toLowerCase()
            ? false
            : cellValue;
    }

    static validateBooleanValue(
        { positiveLabelKey, negativeLabelKey },
        { entity, header }
    ) {
        const value = entity.getIn(header.getAccessor());
        if (!DataUtils.isEmpty(value) && typeof variable !== 'boolean') {
            return nsTranslate('validations:booleanValueValidation', {
                positiveLabel: nsTranslate(positiveLabelKey),
                negativeLabel: nsTranslate(negativeLabelKey)
            });
        }
    }
    static getValidationIds({ entity, header }) {
        const validationIds = entity.getIn(header.getAccessor());
        if (
            Immutable.List.isList(validationIds) &&
            header.getIdForAsyncValidation
        ) {
            return validationIds
                .map(idObj => {
                    return header.getIdForAsyncValidation(idObj);
                })
                .toArray();
        } else if (
            header.getIdForAsyncValidation &&
            isString(header.getIdForAsyncValidation(validationIds))
        ) {
            return [header.getIdForAsyncValidation(validationIds)];
        } else if (isString(validationIds)) {
            return [validationIds];
        }
        return [];
    }
    static getIdErrorMessage({ exisitingEntityIds, validationIds, header }) {
        if (!exisitingEntityIds) {
            return;
        }
        const exisitingEntityIdsSet = new Set();
        exisitingEntityIds.forEach(id => exisitingEntityIdsSet.add(id));
        const entityIdsNotFound = validationIds.filter(
            id => !exisitingEntityIdsSet.has(id)
        );
        let error;
        if (entityIdsNotFound.length) {
            error = nsTranslate('validations:entityIdNotFound', {
                id: entityIdsNotFound.join(', '),
                columnTitle: header.title
            });
        }
        return error;
    }

    static getExistingIdErrorMessage({ exisitingEntityIds, header }) {
        if (!exisitingEntityIds) {
            return;
        }
        let error;
        if (exisitingEntityIds.length) {
            error = nsTranslate('validations:entityIdAlreadyExists', {
                id: exisitingEntityIds.join(', '),
                columnTitle: header.title
            });
        }
        return error;
    }

    static validateEntityIdAsync({
        validator,
        entity,
        header,
        onSuccess,
        onFailure,
        clientId,
        checkIsNewId
    }) {
        const validationIds = EntityImportDataHelper.getValidationIds({
            entity,
            header
        });
        validator({ clientId }, { entityIds: validationIds })
            .then(response => {
                let error;
                if (checkIsNewId) {
                    error = EntityImportDataHelper.getExistingIdErrorMessage({
                        exisitingEntityIds: response.entityIds,
                        validationIds,
                        header
                    });
                } else {
                    error = EntityImportDataHelper.getIdErrorMessage({
                        exisitingEntityIds: response.entityIds,
                        validationIds,
                        header
                    });
                }
                onSuccess({
                    error,
                    entityId: entity.id,
                    headerId: header.id
                });
            })
            .catch(error => {
                if (error instanceof ApiError) {
                    onFailure({
                        error: error.message,
                        entityId: entity.id,
                        headerId: header.id
                    });
                    return;
                }
                onFailure({
                    error: nsTranslate('translations:genericError'),
                    entityId: entity.id,
                    headerId: header.id
                });
            });
    }

    static getCellValidationMessage({
        entity,
        header,
        validationData,
        verifiedRowIds
    }) {
        /*
         1st,
         try error on top level
         (for primitive values)
        */
        // last item in header.accessor
        const fieldAccessor = header.getFieldAccessor();
        const error = entity.getValidationMessage(header.id);
        if (error) {
            return error;
        }
        /*
         2nd,
         try error for 1st level nesting
        */
        const group = entity.get(fieldAccessor);
        const nestedGroupError = EntityImportDataHelper.validateGroup({
            group,
            id: header.id
        });
        if (nestedGroupError) {
            return nestedGroupError;
        }
        /*
         3rd,
         get the most deeply nested group
        */
        // header.accessor with last item removed
        const groupAccessor = header.getGroupAccessor();
        let deepNestedGroupError;
        if (groupAccessor) {
            const nestedGroup = entity.getIn(groupAccessor);
            deepNestedGroupError = EntityImportDataHelper.validateGroup({
                group: nestedGroup,
                id: fieldAccessor
            });
        }
        if (deepNestedGroupError) {
            return deepNestedGroupError;
        }
        /*
            4th,
            header.validateCell()
        */
        let externalError;
        if (header.validateCell) {
            externalError = header.validateCell({
                entity,
                header,
                verifiedRowIds,
                ...validationData
            });
        }
        if (externalError) {
            return externalError;
        }
    }

    static getValue({ entity, header }) {
        return entity.getIn(header.getAccessor());
    }

    static getInvalidOptionError(possibleValues) {
        return nsTranslate(
            'validations:customFieldValidations.notAValidValue',
            {
                possibleValuesList: possibleValues.join(', ')
            }
        );
    }
    static isInvalidDayOfWeek(day) {
        return !DataUtils.isEmpty(day)
            ? !Object.values(DayOfWeekType).includes(day)
            : false;
    }

    static isInvalidWeek(week) {
        return !DataUtils.isEmpty(week)
            ? !Object.values(WeekType).includes(week)
            : false;
    }

    static validateDayOfWeek(args) {
        const value = EntityImportDataHelper.getValue(args);
        return EntityImportDataHelper.isInvalidDayOfWeek(value)
            ? EntityImportDataHelper.getInvalidOptionError(
                  Object.values(DayOfWeekType)
              )
            : null;
    }

    static validateDaysOfWeek(args) {
        const value = EntityImportDataHelper.getValue(args);
        return value?.some(EntityImportDataHelper.isInvalidDayOfWeek)
            ? EntityImportDataHelper.getInvalidOptionError(
                  Object.values(DayOfWeekType)
              )
            : null;
    }

    static validateWeek(args) {
        const value = EntityImportDataHelper.getValue(args);
        return EntityImportDataHelper.isInvalidWeek(value)
            ? EntityImportDataHelper.getInvalidOptionError(
                  Object.values(WeekType)
              )
            : null;
    }

    static validateContact({ entity, header, teams }) {
        const entityContactId = entity.getIn(['contactId', 'contactId']);
        const newContactId = entity.getIn(['contact', 'id']);
        const cellValue = entity.getIn(header.accessor);
        if (
            !entityContactId &&
            !newContactId &&
            cellValue &&
            cellValue.size !== 0
        ) {
            return nsTranslate(
                'entityManagement:masterLocationProperties:newContactIdError'
            );
        }
        if (!entityContactId && newContactId && !cellValue) {
            return nsTranslate(
                'entityManagement:masterLocationProperties.contactFieldError',
                {
                    field: header.title
                }
            );
        }
        if (!entityContactId && newContactId && cellValue) {
            const contact = entity.contact?.setAllValidationErrors();
            let error = null;
            switch (header.id) {
                case 'newContactId':
                    error = cellValue
                        ? contact.getValidationMessage('id')
                        : null;
                    break;
                case 'contactName':
                    error = contact.getValidationMessage('name');
                    break;
                case 'contactNumber':
                    error = contact.getValidationMessage('phoneNumber');
                    break;
                case 'contactType':
                    error = EntityImportDataHelper.validateContactType({
                        entity,
                        header
                    });
                    break;
                case 'contactTeams':
                    error = EntityImportDataHelper.validateContactTeamIds({
                        entity,
                        teams
                    });
                    break;
            }
            return error;
        }
    }

    static validateContactType({ entity, header }) {
        const { contact } = entity;
        const { type } = contact;
        const contactTypes = ContactType.values();
        const isContactTypeValid = contactTypes.find(
            contactType => type?.trim() === contactType
        );

        if (!isContactTypeValid) {
            return nsTranslate(
                'entityManagement:masterLocationProperties.contactTypeError',
                {
                    typeList: contactTypes.join(', '),
                    contactField: header.title
                }
            );
        }
    }

    static validateSkills({ entity, skills, header }) {
        const entitySkills = entity.getIn(header.getAccessor());

        if (entitySkills.size > 0) {
            const skillIds = skills.map(skill => skill.id);
            const invalidSkillsList = entitySkills.filter(
                skill => !skillIds.includes(skill.id)
            );

            if (invalidSkillsList.size > 0) {
                const invalidSkills = invalidSkillsList
                    .map(skill => skill.id)
                    .join(', ');
                return nsTranslate('validations:invalidSkills', {
                    invalidSkills
                });
            }
        }
    }

    /**
     * Gets `workingDaysAccessor` and `dayOfWeekIndex` finds and update `dayIndex` in `accessor`
     * @param {Object}
     */
    static getTimeSlotsAccessor({
        accessor,
        entity,
        workingDaysAccessor,
        dayOfWeekIndex
    }) {
        const dayOfWeek = accessor.get(dayOfWeekIndex);
        const dayIndex = entity
            .getIn(workingDaysAccessor)
            .findIndex(workingDay => dayOfWeek === workingDay.get('dayOfWeek'));
        return accessor.set(dayOfWeekIndex, dayIndex);
    }

    static getCustomResourceAccessor(): Immutable.List<string> {
        const newAccessor = Immutable.List(RESOURCE_PATH);
        return newAccessor;
    }

    static getResourceLimits(
        resourceLimits: Immutable.Map<*>
    ): Immutable.List<ResourceLimitModelType> {
        if (!DataUtils.isEmpty(resourceLimits)) {
            const resources = resourceLimits.map((resource, key) => {
                return {
                    name: key,
                    lowerThresh: resource.get('lowerThresh'),
                    higherThresh: resource.get('higherThresh'),
                    unit: UnitEnum.COUNT
                };
            });
            return resources
                .toList()
                .filter(
                    resource =>
                        !!resource.lowerThresh || !!resource.higherThresh
                );
        }
        return Immutable.List();
    }

    /**
     * This method takes `team` object and @returns `TeamId` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeTeams = (teams: Array): Immutable.List =>
        Immutable.List(teams).map(item => TeamId.fromJS(item));

    /**
     * This method takes `timeSlot` object and @returns `MultiDaySlot` object
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeMultiDaySlot = (timeSlot: Object): MultiDaySlot =>
        MultiDaySlot.fromJS(timeSlot);

    static getModifiedResources = (
        resourceLimits,
        resourceName,
        customFieldsKey,
        value
    ) => {
        const resourceIndex = resourceLimits.findIndex(
            limit => limit.name === resourceName
        );
        if (resourceIndex === -1) {
            const resourceToAdd = ResourceLimit.fromJS({
                name: resourceName,
                unit: UnitEnum.COUNT,
                [customFieldsKey]: value
            });
            return resourceLimits.push(resourceToAdd);
        }
        const resource = resourceLimits.get(resourceIndex);
        const modifiedResource = resource.set(customFieldsKey, value);
        //handling the case of editing resource higher and lower thresh to empty cells in import
        if (
            DataUtils.isEmpty(modifiedResource.lowerThresh) &&
            DataUtils.isEmpty(modifiedResource.higherThresh)
        ) {
            return resourceLimits.deleteIn([resourceIndex]);
        }
        return resourceLimits.setIn([resourceIndex], modifiedResource);
    };

    static serializeCustomResources = (value, props): ResourceLimit => {
        const {
            entity,
            accessor,
            cellId: {
                headerId,
                header: { customFieldsKey }
            }
        } = props;
        const resourceName = headerId.split('-').pop();
        const resourceLimits = entity.getIn(accessor);
        const modifiedResourceLimits = EntityImportDataHelper.getModifiedResources(
            resourceLimits,
            resourceName,
            customFieldsKey,
            value
        );
        return modifiedResourceLimits;
    };

    /**
     * This method takes `slot` object and @returns `TimeSlot` object
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeTimeSlot = (slot: Object): TimeSlot =>
        TimeSlot.fromJS(slot);

    /**
     * This method takes `breaks` object and @returns `BreakShift` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeBreakShift = (breaks: Array): Immutable.List =>
        Immutable.List(breaks).map(item => BreakShift.fromJS(item));

    /**
     * This method takes `skills` object and @returns `SkillId` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeSkills = (skills: Array): Immutable.List =>
        Immutable.List(skills).map(item => SkillId.fromJS(item));

    /**
     * This method takes `contactId` string and @returns `ContactId` object of `Immutable.Map`
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeContactId(contactId: string): ?ContactId {
        return contactId ? ContactId.fromJS({ contactId }) : null;
    }

    /**
     * returns top level accessor for updating contactId
     * @param {Object} { accessor, entity }
     */
    static getContactIdAccessor = ({ accessor }) => accessor.pop();

    /**
     * This method takes `transporterIds` object and @returns `TransporterId` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof EntityImportDataHelper
     */
    static serializeTransporterIds = (transporterIds: Array): Immutable.List =>
        Immutable.List(transporterIds).map(item => TransporterId.fromJS(item));

    /**
     * This method takes `shiftIds` object and @returns `ShiftId` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof VehicleImportHelper
     */
    static serializeShiftIds = (shiftIds: Array): Immutable.List =>
        Immutable.List(shiftIds).map(item => ShiftId.fromJS(item));

    static validateEmailAddress(emailAddress) {
        if (
            !DataUtils.isEmpty(emailAddress) &&
            !StringUtils.isEmail(emailAddress)
        ) {
            return nsTranslate('settings:errorMessage.invalidEmailFormat');
        }
    }

    static processHolidays({ cellValue }) {
        const totalHolidays = cellValue
            .split(',')
            .filter(holidays => holidays.trim())
            .map(holiday => {
                const trimmedHoliday = holiday.trim();
                const week = trimmedHoliday.substring(0, 2).toUpperCase();

                const day = trimmedHoliday.substring(2).toUpperCase();
                return {
                    week,
                    day: day || ' '
                };
            });

        const groupedHolidays = {};
        totalHolidays.forEach(holiday => {
            if (!groupedHolidays[holiday.week]) {
                groupedHolidays[holiday.week] = [];
            }
            groupedHolidays[holiday.week].push(
                WeekDayShortEnum[holiday.day] || holiday.day.toLowerCase()
            );
        });

        const updatedHolidays =
            Object.keys(groupedHolidays).map(key => {
                const week = WeekShortEnum[key] || key;
                const days = groupedHolidays[key];
                return {
                    week,
                    days
                };
            }) || [];
        return updatedHolidays;
    }

    static validateHolidays = ({ entity }) => {
        const { type } = entity;
        const { holidays } = type === 'RIDER' ? entity.pjpData : entity;
        let invalidWeekHolidays = [];
        let invalidWeekDays = [];
        let duplicateHolidaysEntries = [];
        if (holidays) {
            const updatedHolidays = holidays.toJS();
            updatedHolidays.forEach(holiday => {
                if (!WeekFullEnum[holiday.week]) {
                    const invalidWeeks = holiday.days.map(day => {
                        return `${holiday.week}${WeekDayFullEnum[day] || day}`;
                    });
                    invalidWeekHolidays = invalidWeekHolidays.concat(
                        invalidWeeks
                    );
                    if (invalidWeekHolidays.length) {
                        return;
                    }
                }
                const duplicateDays = [];
                const invalidDays = holiday.days
                    .map((day, index) => {
                        if (holiday.days.indexOf(day) !== index) {
                            duplicateDays.push(day);
                        }
                        if (!WeekDayEnum[day]) {
                            return `${
                                WeekFullEnum[holiday.week] || holiday.week
                            }${startCase(day)}`;
                        }
                    })
                    .filter(invalidDay => invalidDay);
                if (invalidDays.length) {
                    invalidWeekDays = invalidWeekDays.concat(invalidDays);
                    return;
                }
                if (duplicateDays.length) {
                    const duplicateHolidays = duplicateDays.map(
                        duplicateDay => {
                            return `${
                                WeekFullEnum[holiday.week] || holiday.week
                            }${WeekDayFullEnum[duplicateDay] || duplicateDay}`;
                        }
                    );
                    duplicateHolidaysEntries = duplicateHolidaysEntries.concat(
                        duplicateHolidays
                    );
                    return;
                }
            });
        }
        if (invalidWeekHolidays.length) {
            return nsTranslate('validations:invalidHolidays', {
                holidays: invalidWeekHolidays.join(',')
            });
        }
        if (invalidWeekDays.length) {
            return nsTranslate('validations:invalidHolidays', {
                holidays: invalidWeekDays.join(',')
            });
        }
        if (duplicateHolidaysEntries.length) {
            return nsTranslate('validations:duplicateHolidays', {
                holidays: duplicateHolidaysEntries.join(',')
            });
        }
    };
    static serializeHolidays = (holidays: Array) => {
        return Immutable.List(holidays.map(holiday => Holiday.fromJS(holiday)));
    };

    static getBulkIdValidationErrors = ({
        results,
        columnsWithValidators,
        entities
    }) => {
        let errors = [];
        entities.forEach(entity => {
            columnsWithValidators.forEach(column => {
                const validationIds = EntityImportDataHelper.getValidationIds({
                    entity,
                    header: column
                });
                if (!results) {
                    if (validationIds && validationIds.length) {
                        errors = [
                            ...errors,
                            {
                                error: null,
                                isLoading: true,
                                entityId: entity.id,
                                headerId: column.id
                            }
                        ];
                    }
                } else {
                    let duplicateEntityIds = {};
                    results.forEach(result => {
                        duplicateEntityIds = {
                            ...duplicateEntityIds,
                            [result.id]: result.response.entityIds
                        };
                    });
                    const newError = EntityImportDataHelper.getIdErrorMessage({
                        exisitingEntityIds: duplicateEntityIds[column.id],
                        validationIds,
                        header: column
                    });
                    if (newError) {
                        errors = [
                            ...errors,
                            {
                                error: newError,
                                entityId: entity.id,
                                headerId: column.id
                            }
                        ];
                    } else {
                        errors = [
                            ...errors,
                            {
                                error: null,
                                entityId: entity.id,
                                headerId: column.id
                            }
                        ];
                    }
                }
            });
        });
        return errors;
    };

    static selectSalesChannelsFromEntitySettings(entitySettings) {
        return entitySettings?.pjpSettings?.salesChannels;
    }

    static getDataForCell = ({
        clientId
    }: {
        clientId: string
    }): { clientId: string } => ({ clientId });

    static getLocationIdAccessor({
        accessor,
        entity,
        hasLocationId = false
    }: {
        accessor: Immutable.List<string>,
        entity: Object,
        hasLocationId?: boolean
    }): Immutable.List<string> {
        if (hasLocationId || entity.locationId) {
            return accessor.push('locationId');
        }
        return accessor;
    }

    static getProductIdAccessor({
        accessor,
        entity,
        hasProductId = false
    }: {
        accessor: Immutable.List<string>,
        entity: Object,
        hasProductId?: boolean
    }): Immutable.List<string> {
        if (hasProductId || entity.productId) {
            return accessor.push('lineItemId');
        }
        return accessor;
    }
    static processMinTransactionTime = ({
        cellValue
    }: {
        cellValue: ?string
    }) => {
        if (DataUtils.isEmpty(cellValue)) {
            return '0';
        }
        if (NumberUtils.isNumeric(cellValue)) {
            return cellValue * 60;
        }
        return cellValue;
    };

    static processVolumeUnitType = ({
        cellValue
    }: {
        cellValue: string
    }): ?string => {
        if (cellValue) {
            const matchedVolumeUnit = volumeUnitOptions.find(
                ({ label }) => label === cellValue
            );
            if (matchedVolumeUnit) {
                return matchedVolumeUnit.value;
            }
            return ' '; // HACK for import when user value from volume unit enum
        }
        return null;
    };

    static validateVolumeUnit(unit: string): ?string {
        const translatedVolumeUnit = nsTranslate(
            'entityManagement:masterRiderPersonaProperties.volumeUnit'
        );
        if (!volumeUnitOptions.map(({ value }) => value).includes(unit)) {
            return nsTranslate(
                'entityManagement:entityValidationErrors.oneOfType',
                {
                    fieldType: translatedVolumeUnit,
                    typeList: volumeUnitOptions
                        .map(({ label }) => label)
                        .join('/')
                }
            );
        }
    }

    static serializeVehicleModelId(modelId: string): ?VehicleModelId {
        return modelId ? VehicleModelId.fromJS({ modelId }) : null;
    }

    static getVehicleModelIdAccessor({
        accessor
    }: {
        accessor: Immutable.List<string>
    }): Immutable.List<string> {
        return accessor.pop();
    }
}

export default EntityImportDataHelper;
